// Merging two duplicate contacts (MOTHER-154; Cameron's card on OLYAA-131): what
// is the contacts' own in the merge (recordMerge.tsx does the rest) over
// POST /api/merge/contacts (apps/server/src/mergeRecords.mjs).
import type { MergeField, MergeRecord } from '@olytic/foundation';

import { contacts } from '../config/objects/contacts';
import { humanize } from './format';
import type { ObjectRow } from './objectsApi';
import { type MergeSpec, mergeActions, mergedRow, mergeFieldsOf, textOf } from './recordMerge';

/** What merging does, as the dialog says it (the PM's wording, MOTHER-154). */
export const CONTACT_MERGE_CONSEQUENCES = [
  'Everything linked to the contact that is merged away moves to the one you keep: meetings, opportunities, audiences and billing contacts.',
  'The other contact goes to the Recycle Bin for 30 days.',
  'Restoring it brings back the contact only, not its links.',
];

const yesNo = (value: unknown) => (value === true || value === 'true' ? 'Yes' : 'No');

/**
 * The fields compared, in the record page's order: the ones it shows that a
 * merge may take from the other contact (the server's MERGE_FIELDS). Account
 * is left out: the contact kept stays on its own account, and the server
 * refuses `account_id` in takeFromOther. Created and Apollo Contact ID are not
 * people's choices; the server fills an empty Apollo id from the other contact.
 */
const MERGED_FIELDS = [
  { key: 'first_name', format: String },
  { key: 'last_name', format: String },
  { key: 'title', format: String },
  { key: 'is_primary_contact', format: yesNo },
  { key: 'source', format: humanize },
  { key: 'email', format: String },
  { key: 'linkedin_url', format: String },
  { key: 'is_linkedin_connection', format: yesNo },
];

/** The record page's label for a field (Title Case, as the page shows it). */
function labelOf(key: string): string {
  const label = contacts.details?.find((field) => field.key === key)?.label;
  return typeof label === 'string' ? label : key;
}

/** A contact's name: first and last, else its email. */
export function contactName(row: ObjectRow): string {
  return [textOf(row.first_name), textOf(row.last_name)].filter(Boolean).join(' ') || textOf(row.name) || textOf(row.email) || 'Unnamed contact';
}

/** A contact as the dialog lists it: its name, with its account and email to tell two of the same name apart. */
function mergeRecordOf(row: ObjectRow): MergeRecord {
  const subtitle = [textOf(row.account_name), textOf(row.email)].filter(Boolean).join(' · ');
  return { id: row.id, name: contactName(row), ...(subtitle ? { subtitle } : {}) };
}

/** The kept contact's name once merged (its first and last name, else its email, merged). */
export function mergedName(survivor: ObjectRow, merged: ObjectRow, takeFromOther: string[]): string {
  return contactName(mergedRow(survivor, merged, takeFromOther, ['first_name', 'last_name', 'email']));
}

export const CONTACT_MERGE: MergeSpec = {
  object: 'contacts',
  url: '/api/merge/contacts',
  singular: contacts.singular,
  consequences: CONTACT_MERGE_CONSEQUENCES,
  withAnotherLabel: 'Merge with Another Contact…',
  fields: MERGED_FIELDS,
  labelOf,
  recordOf: mergeRecordOf,
  mergedName,
};

/** The two contacts' fields for the dialog, labelled and formatted as their record pages show them. */
export const contactMergeFields = (first: ObjectRow, second: ObjectRow): MergeField[] => mergeFieldsOf(CONTACT_MERGE, first, second);

/** The Contacts list's selection-bar action (mergeActions). */
export const contactMergeActions = mergeActions;
